import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, Send } from "lucide-react";
import { toast } from "sonner";
import api, { apiFormatError } from "@/lib/api";
import { US_STATES } from "@/lib/regions";

const OPENING = {
  name: "Atelier Concierge",
  copy: "Bonjour — I'm the Atelier concierge. Ask about series, finishes, dimensions, or lead times, and a real person from our team will reply within one business day.",
};

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", state: "", message: "" });
  const [busy, setBusy] = useState(false);
  const areaRef = useRef(null);

  useEffect(() => {
    if (open && areaRef.current) areaRef.current.focus();
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.message.trim() || !form.state) {
      toast.error("Please share your email, project state and message.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/messages", form);
      setSent(true);
      toast.success("Message received. Our concierge will reply shortly.");
    } catch (e2) {
      toast.error(apiFormatError(e2.response?.data?.detail) || "Could not send.");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setSent(false);
    setForm({ name: "", email: "", state: "", message: "" });
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        className="fixed z-[70] bottom-5 right-5 md:bottom-8 md:right-8 flex items-center gap-2 px-4 py-3 bg-brand-green text-brand-gold border border-brand-gold/40 rounded-full shadow-lg hover:bg-brand-gold hover:text-brand-green transition-colors"
        data-testid="chat-toggle"
        aria-label={open ? "Close concierge" : "Open concierge"}
      >
        {open ? <X size={16} /> : <MessageSquare size={16} />}
        <span className="text-[0.66rem] tracking-[0.24em] uppercase font-medium hidden sm:inline">
          {open ? "Close" : "Concierge"}
        </span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed z-[71] bottom-24 right-5 md:right-8 w-[calc(100vw-2.5rem)] max-w-sm max-h-[calc(100dvh-7rem)] overflow-y-auto overscroll-contain bg-brand-bone border border-brand-green/25 shadow-2xl"
            data-lenis-prevent
            data-testid="chat-panel"
          >
            <div className="bg-brand-green text-brand-bone px-5 py-4">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-brand-gold" aria-hidden />
                <span className="text-[0.62rem] tracking-[0.28em] uppercase text-brand-gold font-medium">
                  Live · By Appointment
                </span>
              </div>
              <div className="mt-2 text-brand-bone text-lg tracking-[-0.01em]">Atelier Concierge</div>
            </div>

            {/* Greeting bubble */}
            <div className="p-5 pb-3">
              <div className="text-[0.6rem] tracking-[0.24em] uppercase text-brand-ink/50 font-medium">
                {OPENING.name}
              </div>
              <div className="mt-2 text-brand-ink/85 text-sm leading-relaxed font-light bg-brand-bone-2 p-3 border-l-2 border-brand-gold">
                {OPENING.copy}
              </div>
            </div>

            {sent ? (
              <div className="p-5 pt-2">
                <div className="border border-brand-green/20 bg-white p-4 text-sm text-brand-ink/85" data-testid="chat-success">
                  <div className="text-brand-gold text-[0.6rem] tracking-[0.24em] uppercase font-medium mb-2">
                    Message Received
                  </div>
                  A member of our atelier will follow up at{" "}
                  <span className="text-brand-green font-medium break-all" data-testid="chat-confirmation-email">{form.email}</span> within one business day.
                </div>
                <button
                  type="button"
                  onClick={reset}
                  className="mt-4 text-[0.66rem] tracking-[0.24em] uppercase text-brand-green hover:text-brand-gold"
                  data-testid="chat-send-another"
                >
                  Send another message →
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="px-5 pb-5 space-y-3">
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name (optional)"
                  className="w-full bg-white border border-brand-ink/15 px-3 py-2.5 text-sm focus:outline-none focus:border-brand-green"
                  data-testid="chat-name"
                />
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email"
                  required
                  className="w-full bg-white border border-brand-ink/15 px-3 py-2.5 text-sm focus:outline-none focus:border-brand-green"
                  data-testid="chat-email"
                />
                <select
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                  required
                  aria-label="Project state"
                  className="w-full bg-white border border-brand-ink/15 px-3 py-2.5 text-sm focus:outline-none focus:border-brand-green"
                  data-testid="chat-state"
                >
                  <option value="" disabled>Project state</option>
                  {US_STATES.map((state) => <option key={state} value={state}>{state}</option>)}
                  <option value="Outside the United States">Outside the United States</option>
                </select>
                <textarea
                  ref={areaRef}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="How can we help with your build?"
                  required
                  rows={4}
                  className="w-full bg-white border border-brand-ink/15 px-3 py-2.5 text-sm focus:outline-none focus:border-brand-green resize-y"
                  data-testid="chat-message"
                />
                <button
                  type="submit"
                  disabled={busy}
                  className={`btn-primary w-full justify-center ${busy ? "opacity-60 pointer-events-none" : ""}`}
                  data-testid="chat-send"
                >
                  {busy ? "Sending…" : (<><Send size={13} /> Send Message</>)}
                </button>
                <p className="text-[0.58rem] tracking-[0.14em] uppercase text-brand-ink/45 text-center">
                  Replies typically within 1 business day
                </p>
              </form>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
